import React, { useState } from 'react';
import { FaTrash, FaPen, FaGraduationCap } from 'react-icons/fa';
import { SectionCard, Field, TextInput, Select, SaveRow, AddButton, EmptyHint, palette } from './ui';
import { EDUCATION_LEVELS, DEGREE_LEVELS } from './constants';

const EMPTY_FORM = { level: '', degree: '', specialization: '', institution: '', passing_year: '' };

function EducationForm({ isDarkMode, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const showDegree = DEGREE_LEVELS.includes(form.level);

  const save = async () => {
    if (!form.level) return;
    setSaving(true);
    const ok = await onSave({
      ...form,
      degree: showDegree ? form.degree : '',
      specialization: showDegree ? form.specialization : '',
      passing_year: form.passing_year === '' ? null : Number(form.passing_year),
    });
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
      <Field label="Education level *">
        <Select isDarkMode={isDarkMode} value={form.level} onChange={set('level')} options={EDUCATION_LEVELS} placeholder="Select level" />
      </Field>
      <Field label="Passing year">
        <TextInput isDarkMode={isDarkMode} type="number" min="1950" max="2035" value={form.passing_year} onChange={set('passing_year')} placeholder="e.g. 2020" />
      </Field>
      {showDegree && (
        <>
          <Field label="Degree / Course">
            <TextInput isDarkMode={isDarkMode} value={form.degree} onChange={set('degree')} placeholder="e.g. B.Com, ITI Electrician" />
          </Field>
          <Field label="Specialization">
            <TextInput isDarkMode={isDarkMode} value={form.specialization} onChange={set('specialization')} placeholder="e.g. Accounting" />
          </Field>
        </>
      )}
      <Field label="School / College / Institute" className="sm:col-span-2">
        <TextInput isDarkMode={isDarkMode} value={form.institution} onChange={set('institution')} placeholder="Institution name" />
      </Field>
      <div className="sm:col-span-2">
        <SaveRow isDarkMode={isDarkMode} onSave={save} onCancel={onCancel} saving={saving} />
      </div>
    </div>
  );
}

export default function EducationCard({ isDarkMode, items, actions }) {
  const c = palette(isDarkMode);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const levelLabel = (v) => (EDUCATION_LEVELS.find(l => l.value === v) || {}).label || v;

  return (
    <SectionCard id="education" title="Education" isDarkMode={isDarkMode}>
      {items.length === 0 && !adding && (
        <EmptyHint isDarkMode={isDarkMode}>Add your highest education — 10th pass, ITI, diploma or degree.</EmptyHint>
      )}
      <ul className="space-y-4">
        {items.map(item => (
          <li key={item.id} className="flex gap-3">
            <FaGraduationCap className={`h-4 w-4 mt-1 shrink-0 ${c.secondaryText}`} />
            <div className="flex-1 min-w-0">
              {editingId === item.id ? (
                <EducationForm
                  isDarkMode={isDarkMode}
                  initial={{ ...EMPTY_FORM, ...item, passing_year: item.passing_year ?? '' }}
                  onSave={(data) => actions.update(item.id, data)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <>
                  <p className={`font-medium text-sm ${c.text}`}>
                    {[levelLabel(item.level), item.degree].filter(Boolean).join(' · ')}
                  </p>
                  <p className={`text-sm ${c.secondaryText}`}>
                    {[item.specialization, item.institution, item.passing_year].filter(Boolean).join(' · ')}
                  </p>
                </>
              )}
            </div>
            {editingId !== item.id && (
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { setEditingId(item.id); setAdding(false); }} aria-label="Edit" className={`p-2 rounded-full ${c.hover} ${c.secondaryText}`}>
                  <FaPen className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => actions.remove(item.id)} aria-label="Delete" className={`p-2 rounded-full ${c.hover} text-red-500`}>
                  <FaTrash className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {adding ? (
        <EducationForm isDarkMode={isDarkMode} initial={EMPTY_FORM} onSave={(data) => actions.add(data)} onCancel={() => setAdding(false)} />
      ) : (
        <div className="mt-3">
          <AddButton isDarkMode={isDarkMode} label="Add education" onClick={() => { setAdding(true); setEditingId(null); }} />
        </div>
      )}
    </SectionCard>
  );
}
